import React, { useState } from 'react';
import { useNotification } from '../../../../../../contexts/NotificationContext';
import { useConfirm } from '../../../../../../contexts/ConfirmContext';
import { usePageLoaderContext } from '../../../../../../contexts/PageLoaderContext';
import apiService from '../../../../../../services/api';
import buttonStyles from '../../../../../../styles/Buttons.module.css';
import styles from '../../../../../../styles/SettingsTab.module.css';

interface DangerZoneProps {
  groupId: string;
  groupName: string;
  loading: boolean;
  onGroupUpdate: () => void;
}

const DangerZone: React.FC<DangerZoneProps> = ({
  groupId,
  groupName,
  loading,
  onGroupUpdate
}) => {
  const notificationService = useNotification();
  const confirmService = useConfirm();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleDeleteGroup = async () => {
    if (!deleteConfirmation.trim()) {
      notificationService.warning('Please enter the group name in the confirmation field first.');
      return;
    }

    if (deleteConfirmation !== groupName) {
      notificationService.error(`The entered name "${deleteConfirmation}" does not match the group name "${groupName}". Please try again.`);
      return;
    }

    const finalConfirmation = await confirmService.show(
      'Delete Group',
      `WARNING: This will permanently delete the group "${groupName}" and all its data. This action cannot be undone. Are you absolutely sure?`,
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (finalConfirmation) {
      showLoader('Deleting group...');

      try {
        const response = await apiService.deleteGroup(groupId, {
          confirmation_name: deleteConfirmation || ''
        });

        if (response.status === 'success') {
          notificationService.success(response.message || 'Group deleted successfully');
          setTimeout(() => {
            window.location.href = '/group-management/groups';
          }, 2000);
        } else {
          notificationService.error(response.message || 'Failed to delete group');
        }
      } catch (error: any) {
        console.error('Error deleting group:', error);
        notificationService.error(error.response?.data?.message || 'Failed to delete group. Please try again.');
      } finally {
        hideLoader();
      }
    }
  };


  return (
    <div className={styles.settingsSections}>
      <div className={`${styles.settingsSection} ${styles.dangerSection}`}>
        <div>
          <span className={styles.sectionBadge} style={{ background: 'var(--danger)' }}>Danger</span>
          <h3 className={styles.sectionTitle}>
            <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            Group Deletion
          </h3>
          <p style={{ color: 'var(--gray)', marginBottom: '1.5rem' }}>
            Permanently delete this group and all its associated data. This action cannot be undone.
          </p>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="deleteConfirmation">Type the group name to confirm deletion</label>
            <input
              type="text"
              className={styles.formControl}
              id="deleteConfirmation"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Group Name"
            />
            <div className={styles.formHelp}>This helps prevent accidental deletions</div>
          </div>
        </div>
        <div className={styles.dangerActionContainer}>
          <button
            className={`${buttonStyles.btn} ${buttonStyles.btnDanger}`}
            onClick={handleDeleteGroup}
            disabled={loading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3,6 5,6 21,6"></polyline>
              <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
            </svg>
            {loading ? 'Deleting...' : 'Delete Group'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DangerZone;

